import React, { type FC, type ReactNode } from 'react';
import classnames from 'classnames';
import { createPictureFactory, Picture } from '@funtech-int/yango-shared';

import styles from './ImageInfoBlock.module.css';

const backgroundPictureFactory = createPictureFactory({
    fallback: {
        srcSet: {
            '1x': '1920x1080_95q',
            '2x': '3840x2160',
        },
        src: '1920x1080_95q',
    },
});

const contentPictureFactory = createPictureFactory({
    fallback: {
        src: '720x',
    },
});

type BlockAlignment = 'start' | 'end';

interface ImageInfoBlockProps {
    alignment: BlockAlignment;
    dataTid?: string;
    contentImage?: string;
    title?: string;
    subtitle?: string;
    subtitleMaxWidth?: number;
    description?: string;
    descriptionMaxWidth?: number;
    backgroundImage?: string;
    foregroundImage?: string;
    primaryButton?: ReactNode;
    secondaryButton?: ReactNode;
    footerButton?: ReactNode;
}

export const ImageInfoBlock: FC<ImageInfoBlockProps> = ({
    backgroundImage,
    foregroundImage,
    contentImage,
    primaryButton,
    secondaryButton,
    subtitle,
    subtitleMaxWidth,
    title,
    description,
    descriptionMaxWidth,
    footerButton,
    alignment,
    dataTid,
}) => {
    const { img: backgroundImg } = backgroundPictureFactory({
        fallbackUrl: backgroundImage,
    });

    const { img: foregroundImg } = backgroundPictureFactory({
        fallbackUrl: foregroundImage,
    });

    const { img: contentImg } = contentPictureFactory({
        fallbackUrl: contentImage,
    });

    return (
        <section className={styles.root} data-tid={dataTid ?? 'ImageInfoBlock'}>
            <div className={styles.container}>
                {backgroundImage && (
                    <Picture
                        className={styles.backgroundImage}
                        alt=""
                        loading="lazy"
                        srcSet={backgroundImg.srcSet}
                        src={backgroundImg.src}
                        data-tid="AdaptiveImage"
                    />
                )}
                {foregroundImage && (
                    <Picture
                        className={styles.foregroundImage}
                        alt=""
                        loading="lazy"
                        srcSet={foregroundImg.srcSet}
                        src={foregroundImg.src}
                        data-tid="AdaptiveImage"
                    />
                )}
                <div className={classnames(styles.content, styles[`content_align_${alignment}`])}>
                    {title && <h2 className={styles.title}>{title}</h2>}
                    {subtitle && (
                        <p
                            className={styles.subtitle}
                            style={
                                subtitleMaxWidth ? { maxWidth: `${subtitleMaxWidth}px` } : undefined
                            }
                        >
                            {subtitle}
                        </p>
                    )}
                    {description && (
                        <p
                            className={styles.description}
                            style={
                                descriptionMaxWidth
                                    ? { maxWidth: `${descriptionMaxWidth}px` }
                                    : undefined
                            }
                        >
                            {description}
                        </p>
                    )}
                    {contentImage && (
                        <Picture
                            className={styles.contentImage}
                            alt=""
                            loading="lazy"
                            srcSet={contentImg.srcSet}
                            src={contentImg.src}
                            data-tid="AdaptiveImage"
                        />
                    )}
                    {(primaryButton || secondaryButton) && (
                        <div className={styles.buttonsContainer}>
                            {primaryButton}

                            {secondaryButton}
                        </div>
                    )}
                </div>
                <div className={classnames(styles.footer, styles[`footer_align_${alignment}`])}>
                    {footerButton}
                </div>
            </div>
        </section>
    );
};
